import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import "./tenant-filter.scss";
import { tenantFilterOptions as defaultFilterOptions } from "./tenant-filter-helper";
import { Formik, Form } from "formik";
import CompanyNameFilter from "./company-name";
import EmailFilter from "./email-filter";
import TimezoneFilter from "./timezone-filter";
import { getTimezones } from "../../../../constants/timezones";
import StatusFilter from "./status-filter";
import OnboardedDateFilter from "./onboardeddate-filter";
import SubscriptionPlanFilter from "./subscription-plan";
import { CustomCheckbox } from "../../../../common/components";
import "./filter-options-tenant.scss";
import dayjs from "dayjs";
import AddressFilter from "./address-filter";

const TenantFilterOptions: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Record<string, any>, shouldClose?: boolean) => void;
  filters: Record<string, any>;
  companyNames?: string[];
}> = ({ open, onClose, onApplyFilters, filters, companyNames }) => {
  const [filterOptions, setFilterOptions] = useState(() =>
    defaultFilterOptions.map((opt) => ({ ...opt }))
  );

  const timezones = getTimezones();

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          if (label === "Onboarded date") {
            setFieldValue("onBoardedAtStart", null);
            setFieldValue("onBoardedAtEnd", null);
          } else if (label === "Payment Pending") {
            setFieldValue("paymentPending", false);
          } else if(label === "Address") {
            setFieldValue("country", "");
            setFieldValue("state", "");
            setFieldValue("city", "");
            setFieldValue("postcode", "");
          } else {
            const key = toCamelCase(label);
            setFieldValue(key, Array.isArray(values[key]) ? [] : "");
          }
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);

  const toCamelCase = (label: string) =>
    label
      .toLowerCase()
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
        index === 0 ? word.toLowerCase() : word.toUpperCase()
      )
      .replace(/\s+/g, "");

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      if (option.label === "Onboarded date") {
        values["onBoardedAtStart"] = null;
        values["onBoardedAtEnd"] = null;
      } else if (option.label === "Payment Pending") {
        values["paymentPending"] = false;
      } else if(option?.label === "Address") {
        values["country"] = "";
        values["state"] = "";
        values["city"] = "";
        values["postcode"] = "";
      } else {
        const key = toCamelCase(option.label);
        if (
          option.label === "Status" ||
          option.label === "Subscription Plan" ||
          option.label === "Timezone"
        ) {
          values[key] = [];
        } else {
          values[key] = "";
        }
      }
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          if (curr.label === "Onboarded date") {
            acc["onBoardedAtStart"] = values["onBoardedAtStart"];
            acc["onBoardedAtEnd"] = values["onBoardedAtEnd"];
          } else if (curr.label === "Payment Pending") {
            acc["paymentPending"] = values["paymentPending"];
          } else if (curr?.label === "Address") {
            if (values["country"]) acc["country"] = values["country"];
            if (values["state"]) acc["state"] = values["state"];
            if (values["city"]) acc["city"] = values["city"];
            if (values["postcode"]) acc["postcode"] = values["postcode"];
          } else {
            const key = toCamelCase(curr.label);
            acc[key] = values[key];
          }
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");

    if (!Array.isArray(filterOptions)) return errors;

    filterOptions.forEach((option) => {
      if (!option?.value) return;

      const key = toCamelCase(option.label);

      if (option.label === "Onboarded date") {
        const startRaw = values["onBoardedAtStart"];
        const endRaw = values["onBoardedAtEnd"];

        const startDate = startRaw ? dayjs(startRaw) : null;
        const endDate = endRaw ? dayjs(endRaw) : null;

        if (!startDate || !startDate.isValid()) {
          errors["onBoardedAtStart"] = "Start date is required";
        } else if (startDate.isBefore(minDate)) {
          errors["onBoardedAtStart"] = "Invalid start date";
        }
        if (!endDate || !endDate.isValid()) {
          errors["onBoardedAtEnd"] = "End date is required";
        } else if (endDate.isBefore(minDate)) {
          errors["onBoardedAtEnd"] = "Invalid end date";
        }
      } else if (option?.label === "Address") {
        // There should be at least one field selected in address
        if (!values["country"] && !values["state"] && !values["city"] && !values["postcode"]) {
          errors["country"] = "At least one address field must be filled";
        }
      } else {
        const fieldValue = values[key];
        if (
          !fieldValue ||
          (Array.isArray(fieldValue) && fieldValue.length === 0)
        ) {
          errors[key] = `${option.label} cannot be empty`;
        }
      }
    });

    return errors;
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void
  ) => {
    switch (label) {
      case "Company Name":
        return (
          <CompanyNameFilter
            isCompanyNameEnabled={true}
            companyNames={companyNames || []}
            value={values["companyName"] || []}
            error={errors?.companyName}
            onChange={(selectedValues) =>
              setFieldValue?.("companyName", selectedValues)
            }
          />
        );
      case "Email":
        return (
          <EmailFilter
            isEmailEnabled={true}
            value={values["email"]}
            error={errors?.email}
            onChange={(e: any) => setFieldValue?.("email", e.target.value)}
          />
        );
      case "Timezone":
        return (
          <TimezoneFilter
            isTimezoneEnabled={true}
            value={values["timezone"]}
            error={errors?.timezone}
            timezones={timezones}
            onChange={(val) => setFieldValue?.("timezone", val)}
          />
        );
      case "Status":
        return (
          <StatusFilter
            isStatusEnabled={true}
            value={values["status"] || []}
            error={errors?.status}
            onChange={(val) => setFieldValue?.("status", val)}
          />
        );
      case "Onboarded date":
        return (
          <OnboardedDateFilter
            startDate={values.onBoardedAtStart}
            endDate={values.onBoardedAtEnd}
            onDateRangeChange={(start, end) => {
              if (start) setFieldValue?.("onBoardedAtStart", start);
              if (end) setFieldValue?.("onBoardedAtEnd", end);
            }}
            startDateError={
              touched?.onBoardedAtStart && errors?.onBoardedAtStart
                ? String(errors.onBoardedAtStart)
                : undefined
            }
            endDateError={
              touched?.onBoardedAtEnd && errors?.onBoardedAtEnd
                ? String(errors.onBoardedAtEnd)
                : undefined
            }
          />
        );
      case "Subscription Plan":
        return (
          <SubscriptionPlanFilter
            value={
              Array.isArray(values["subscriptionPlan"])
                ? values["subscriptionPlan"]
                : []
            }
            plans={[
              "Basic",
              "Professional",
              "Enterprise",
              "Starter",
              "No Cost",
              "Trial",
            ]}
            error={errors?.subscriptionPlan}
            onChange={(val: string[]) =>
              setFieldValue?.("subscriptionPlan", val)
            }
            isMultiple={true}
          />
        );
      case "Payment Pending":
        return (
          <div className="filter-options-tenant-filters-payment-pending">
            <div className="filter-options-tenant-filters">
              <CustomCheckbox
                checked={values["paymentPending"]}
                onChange={(e) => setFieldValue?.("paymentPending", e)}
                label="Show tenants with pending payments"
              />
            </div>
            {touched?.paymentPending && errors?.paymentPending && (
              <div className="error-message">{errors.paymentPending}</div>
            )}
          </div>
        );
      case "Address":
        return (
          <>
          <AddressFilter
            values={{
              country: values["country"] || "",
              state: values["state"] || "",
              city: values["city"] || "",
              postcode: values["postcode"] || "",
            }}
            
            setFieldValue={setFieldValue}
            />
          { ((touched?.country || touched?.state || touched?.city || touched?.postcode) && errors?.country) && (
              <div className="error-message" style={{marginLeft:"12px"}}>
                {errors.country}
              </div>
          )}
        
          </>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const updatedOptions = defaultFilterOptions.map((opt) => {
      let key =
        opt.label === "Onboarded date"
          ? "onBoardedAtStart" in filters || "onBoardedAtEnd" in filters
          : toCamelCase(opt.label) in filters;
      if (opt.label === "Address") {
        key = "country" in filters || "state" in filters || "city" in filters || "postcode" in filters;
      }
      return { ...opt, value: !!key };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div className={`filter-drawer ${open ? "open" : ""}`}>
      <div className="filter-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon
            fontSize="small"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
        <h4>Filter By Fields</h4>
      </div>

      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
        }) => {
          return (
            <Form className="drawer-content" onSubmit={handleSubmit}>
              {filterOptions.map((option) => (
                <div key={option.label} className="filter-option-row">
                  <MenuItem
                    key={option.label}
                    value={option.label}
                    className="filter-menu-item"
                    onClick={() =>
                      handleCheckboxChange(option.label, values, setFieldValue)
                    }
                  >
                    <Checkbox checked={option.value} />
                    <ListItemText primary={option.label} />
                  </MenuItem>

                  {option.value &&
                    renderFilterField(
                      option.label,
                      values,
                      errors,
                      touched,
                      setFieldValue
                    )}
                </div>
              ))}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        defaultFilterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({});
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default TenantFilterOptions;
